import React from 'react';
import { Checkbox, Col, Row, Typography } from 'antd';
import type { PermissionFeatureCatalogItem } from '../api/roles';

const { Text } = Typography;

interface RolePermissionMatrixProps {
  catalog: PermissionFeatureCatalogItem[];
  value: string[];
  onChange: (permissions: string[]) => void;
  disabled?: boolean;
}

function permissionActionLabel(permission: string): string {
  const parts = permission.split('.');
  const action = parts.length > 1 ? parts.slice(1).join(' ') : permission;
  return action.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

const RolePermissionMatrix: React.FC<RolePermissionMatrixProps> = ({
  catalog,
  value,
  onChange,
  disabled,
}) => {
  const toggle = (permission: string, checked: boolean) => {
    if (checked) {
      onChange([...new Set([...value, permission])]);
    } else {
      onChange(value.filter((p) => p !== permission));
    }
  };

  const toggleFeature = (featurePermissions: string[], checked: boolean) => {
    if (checked) {
      onChange([...new Set([...value, ...featurePermissions])]);
    } else {
      const remove = new Set(featurePermissions);
      onChange(value.filter((p) => !remove.has(p)));
    }
  };

  return (
    <div>
      {catalog.map((feature) => {
        const perms = feature.permissions;
        const allSelected = perms.every((p) => value.includes(p));
        const someSelected = perms.some((p) => value.includes(p));

        return (
          <div key={feature.featureKey} style={{ marginBottom: 16 }}>
            <Checkbox
              disabled={disabled}
              indeterminate={someSelected && !allSelected}
              checked={allSelected}
              onChange={(e) => toggleFeature(perms, e.target.checked)}
            >
              <Text strong>{feature.label}</Text>
            </Checkbox>
            <Row gutter={[8, 8]} style={{ marginTop: 8, marginLeft: 24 }}>
              {perms.map((permission) => (
                <Col key={permission} span={6}>
                  <Checkbox
                    disabled={disabled}
                    checked={value.includes(permission)}
                    onChange={(e) => toggle(permission, e.target.checked)}
                  >
                    {permissionActionLabel(permission)}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </div>
        );
      })}
    </div>
  );
};

export default RolePermissionMatrix;
